import Image from "../../../components/AppImage";
import Icon from "../../../components/AppIcon";
import { Testimonial, SocialLink } from "../types";

interface SocialProofProps {
  testimonials: Testimonial[];
  socialLinks: SocialLink[];
}

const SocialProof = ({ testimonials, socialLinks }: SocialProofProps) => {
  return (
    <section className="py-20 bg-surface/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-secondary/10 border border-secondary/20 rounded-full mb-4">
            <Icon name="Users" size={18} color="var(--color-secondary)" />
            <span className="text-sm font-mono text-secondary">Community &amp; Network</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-foreground mb-4">
            What People Say
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Feedback from classmates, collaborators, and mentors
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {testimonials.map((testimonial, index) => (
            <div
              key={testimonial.id}
              className="bg-surface border border-border rounded-2xl p-6 hover:border-primary/50 transition-all duration-300 hover:shadow-elevation-2 animate-fade-in-up"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-full overflow-hidden bg-muted flex-shrink-0">
                  <Image
                    src={testimonial.avatar}
                    alt={testimonial.alt}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <h4 className="font-semibold text-foreground">{testimonial.name}</h4>
                  <p className="text-sm text-muted-foreground">
                    {testimonial.role} at {testimonial.company}
                  </p>
                </div>
              </div>
              <p className="text-muted-foreground leading-relaxed italic">
                "{testimonial.content}"
              </p>
            </div>
          ))}
        </div>

        <div className="text-center">
          <h3 className="text-2xl font-bold text-foreground mb-6">Connect With Me</h3>
          <div className="flex flex-wrap items-center justify-center gap-4">
            {socialLinks.map((link) => (
              <a
                key={link.id}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-3 px-6 py-3 bg-surface border border-border rounded-xl hover:border-primary/50 transition-all duration-300 hover:shadow-elevation-2"
                aria-label={link.label}
              >
                <Icon name={link.icon} size={20} color="var(--color-primary)" />
                <span className="font-medium text-foreground group-hover:text-primary transition-colors duration-300">
                  {link.platform}
                </span>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SocialProof;